import React, { useEffect } from 'react';
import { X, Navigation, Route, Map as MapIcon, ChevronRight, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CampingSpot, Coordinates } from '../../types';
import { useFocusTrap } from '../../hooks/useFocusTrap';

interface NavigationChoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNativeNav: () => void;
  onInAppNav: () => void;
  onCopyCoords?: () => void;
  coords?: Coordinates;
  targetName?: string;
}

const NavigationChoiceModal: React.FC<NavigationChoiceModalProps> = ({
  isOpen,
  onClose,
  onNativeNav,
  onInAppNav,
  onCopyCoords,
  coords,
  targetName,
}) => {
  const { t } = useTranslation();
  const trapRef = useFocusTrap<HTMLDivElement>(isOpen);

  // Esc-to-close + focus trap + aria-modal — this dialog previously had none
  // (design-review finding C2). Only ProfileOverlay was accessible before.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-max flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="navchoice-title"
        className="w-full max-w-sm bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-panel overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative p-6 pb-2 text-center">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-input)] text-[var(--text-secondary)] transition-colors"
          >
            <X size={18} />
          </button>
          
          <div className="w-14 h-14 bg-blue-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Navigation size={28} className="text-blue-500" />
          </div>
          
          <h3 id="navchoice-title" className="text-xl font-black text-[var(--text-primary)] uppercase tracking-tight leading-tight">
            {t('navigation.choice_title', 'Vis veg')}
          </h3>
          {targetName && (
            <p className="text-xs font-bold text-[var(--text-muted)] mt-1 uppercase tracking-widest truncate px-4">
              til {targetName}
            </p>
          )}
        </div>

        {/* Options */}
        <div className="p-4 space-y-3">
          <button
            onClick={() => {
              onNativeNav();
              onClose();
            }}
            className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all group active:scale-[0.98]"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 group-hover:scale-110 transition-transform">
              <MapIcon size={20} />
            </div>
            <div className="flex-1 text-left">
              <div className="text-[13px] font-black text-white uppercase tracking-tight">
                {t('navigation.open_in_maps', 'Åpne i kart-app')}
              </div>
              <div className="text-2xs font-bold text-white/50 uppercase tracking-widest mt-0.5">
                {t("auto.navigation.navigationchoicemoda.google_apple_maps", "Google / Apple Maps")}
              </div>
            </div>
            <ChevronRight size={16} className="text-white/20 group-hover:text-white/50 transition-colors" />
          </button>

          <button
            onClick={() => {
              onInAppNav();
              onClose();
            }}
            className="w-full flex items-center gap-4 p-4 rounded-2xl bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-900/20 transition-all group active:scale-[0.98]"
          >
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-white group-hover:scale-110 transition-transform">
              <Route size={20} />
            </div>
            <div className="flex-1 text-left">
              <div className="text-[13px] font-black text-white uppercase tracking-tight">
                {t('navigation.navigate_in_app', 'I appen')}
              </div>
              <div className="text-2xs font-bold text-white/70 uppercase tracking-widest mt-0.5">
                {t("auto.navigation.navigationchoicemoda.fricamp_ruteplanlegger", "Fricamp ruteplanlegger")}
              </div>
            </div>
            <ChevronRight size={16} className="text-white/40 group-hover:text-white transition-colors" />
          </button>

          {onCopyCoords && coords && (
            <button
              onClick={() => {
                onCopyCoords();
                onClose();
              }}
              className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all group active:scale-[0.98]"
            >
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform">
                <Copy size={20} />
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-[13px] font-black text-white uppercase tracking-tight">
                  {t('navigation.copy_coords', 'Kopier koordinater')}
                </div>
                <div className="text-2xs font-bold text-white/50 tracking-wider mt-0.5 truncate">
                  {coords.latitude.toFixed(6)}, {coords.longitude.toFixed(6)}
                </div>
              </div>
              <ChevronRight size={16} className="text-white/20 group-hover:text-white/50 transition-colors" />
            </button>
          )}
        </div>

        {/* Info */}
        <div className="px-8 py-6 bg-black/20 text-center">
            <p className="text-2xs font-bold text-[var(--text-muted)] uppercase tracking-[0.1em] leading-relaxed">
                {t("auto.navigation.navigationchoicemoda.velg_navigasjonsmetode_in_app_", "Velg navigasjonsmetode. In-app ruteplanlegger krever internett for beregning.")}
            </p>
        </div>
      </div>
    </div>
  );
};

export default NavigationChoiceModal;
